import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addMonitor, updateMonitor } from "@/features/eservices/api";
import type { ForWho, TranscriptMonitor } from "@/features/eservices/types";

interface MonitorDialogProps {
  open: boolean;
  edit: TranscriptMonitor | null;
  clientId: string;
  hasSpouse: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

const CURRENT_YEAR = new Date().getFullYear();

export function MonitorDialog({
  open,
  edit,
  clientId,
  hasSpouse,
  onOpenChange,
  onSaved,
}: MonitorDialogProps) {
  const [forWho, setForWho] = useState<ForWho>("T");
  const [form, setForm] = useState("SUMMARY");
  const [yearFrom, setYearFrom] = useState(String(CURRENT_YEAR - 5));
  const [yearTo, setYearTo] = useState(String(CURRENT_YEAR));
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (edit) {
      setForWho(edit.forWho);
      setForm(edit.form);
      setYearFrom(edit.yearFrom ? String(edit.yearFrom) : "");
      setYearTo(edit.yearTo ? String(edit.yearTo) : "");
      setIsActive(edit.isActive);
    } else {
      setForWho("T");
      setForm("SUMMARY");
      setYearFrom(String(CURRENT_YEAR - 5));
      setYearTo(String(CURRENT_YEAR));
      setIsActive(true);
    }
  }, [open, edit]);

  const submit = async () => {
    setError("");
    const from = yearFrom ? Number(yearFrom) : null;
    const to = yearTo ? Number(yearTo) : null;
    if (from && to && to < from) return setError("Year range is invalid.");
    setSubmitting(true);
    try {
      const payload = { forWho, form, yearFrom: from, yearTo: to, isActive };
      if (edit) await updateMonitor(clientId, edit.id, payload);
      else await addMonitor(clientId, payload);
      toast.success(edit ? "Monitor updated." : "Monitor added.");
      onSaved();
    } catch (e) {
      // The backend rejects a partial overlap with a clear message.
      setError(e instanceof Error ? e.message : "Could not save the monitor.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{edit ? "Edit monitor" : "Add monitor"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>For</Label>
            <Select value={forWho} onValueChange={(v) => setForWho(v as ForWho)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="T">Taxpayer</SelectItem>
                {hasSpouse && <SelectItem value="S">Spouse</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Transcript</Label>
            <Select value={form} onValueChange={setForm}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SUMMARY">Account summary</SelectItem>
                <SelectItem value="INCOME">Wage &amp; income</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mf">Year from</Label>
              <Input
                id="mf"
                inputMode="numeric"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mt">Year to</Label>
              <Input
                id="mt"
                inputMode="numeric"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="active"
              checked={isActive}
              onCheckedChange={(v) => setIsActive(Boolean(v))}
            />
            <Label htmlFor="active" className="text-sm">
              Active (checked weekly)
            </Label>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
